import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import MyCollapse from 'components/Collapse';
import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import { Field, Form, Formik } from 'formik';
import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import { HOST_ORBITA } from 'settings';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import affiliationsService from 'v2/services/affiliations';

import styles from './styles';
import RecruitmentSchema from './validations';

import type { IRecruitment } from './interfaces';
import type { FormikProps } from 'formik';

interface IProps {
  open: boolean;
  closeModal: Function;
}

const InitialValues = {
  email: '',
};

const RecruitmentCommission: React.FC<IProps> = ({ open, closeModal }) => {
  const classes = styles({});

  const [isLoading] = useObservable(() => affiliationsService.isLoadingRecruitment(), []);
  const [product] = useObservable(() => affiliationsService.getProductRecruitment(), []);

  const onSubmit = useCallback(
    (values: IRecruitment, { setSubmitting, resetForm }: FormikProps<IRecruitment>) => {
      setSubmitting(true);

      affiliationsService.inviteAffiliationByEmail(product, values.email).subscribe(
        () => {
          toast.success('Convite enviado com sucesso.');
          setSubmitting(false);
          resetForm();
        },
        (err) => {
          toast.error(err);
          setSubmitting(false);
        }
      );
    },
    [product]
  );

  if (!open) {
    return null;
  }

  return (
    <MyModal
      title='Comissão por recrutamento'
      hasActive
      hideButtonOk
      hasButtons
      textButtonCancel='Fechar'
      loading={isLoading}
      closeModal={() => closeModal()}
    >
      <div className={classes.modal}>
        {(isLoading === null || isLoading) && <Spin />}

        {!isLoading && !product && (
          <Typography variant='body2' display='block'>
            Algo deu errado, tente novamente mais tarde...
          </Typography>
        )}

        {!isLoading && product && (
          <>
            <div className={classes.commission}>
              Este conteúdo paga comissão por Indicações de Afiliados:
              <br />
              {product.recruit_commission_type && product.recruit_commission_type === 'P' && (
                <>
                  Comissão: <b>{product.recruit_commission_value}%</b>
                  {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                </>
              )}
              {product.recruit_commission_type && product.recruit_commission_type !== 'P' && (
                <>
                  Comissão: <b>R${product.recruit_commission_value}</b>
                  {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                </>
              )}
            </div>

            <div className='m16'></div>

            <MyCollapse title='Convite por link' arrow contentActive>
              <CustomCopyToClipboard
                disableUnderline
                text={`${HOST_ORBITA}/affiliate/invite/${product.id}/${localStorage.getItem('id')}`}
                fullWidth
              />

              <div className='m8'></div>

              <Typography variant='caption' display='block'>
                Divulgue esse link para que se cadastrem automaticamente atrelados ao seu conteúdo.
              </Typography>
            </MyCollapse>

            <div className='m8'></div>

            <MyCollapse title='Convidar por e-mail' arrow>
              <Formik initialValues={InitialValues} validationSchema={RecruitmentSchema} onSubmit={onSubmit}>
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form>
                    <Field
                      id='email'
                      name='email'
                      fullWidth
                      disabled={isSubmitting}
                      component={TextFormField}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('email', e.target.value)}
                      InputProps={{
                        disableUnderline: true,
                        endAdornment: (
                          <InputAdornment position='end'>
                            <Button size='small' type='submit' disabled={isSubmitting || !values.email}>
                              {isSubmitting && <CircularProgress size={16} />}
                              {!isSubmitting && 'Enviar'}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Form>
                )}
              </Formik>

              <div className='m8'></div>

              <Typography variant='caption' display='block'>
                O afiliado precisa estar cadastrado na Eduzz.
              </Typography>
            </MyCollapse>
          </>
        )}
      </div>
    </MyModal>
  );
};

export default memo(RecruitmentCommission);
